// Every keyboard shortcut and gesture, mirroring frontend/ui/shortcuts.py.
//
// tests/unit/shortcuts.test.ts compares this table to tests/golden/shortcuts.json
// (generated from the Python file), so the two cannot drift: a shortcut added on
// the desktop fails the web tests until it is added here, and the only permitted
// differences are the browser remaps listed in REMAPS.

export type Scope = "global" | "annotate" | "canvas" | "gesture";
export type BrowserVerdict = "keep" | "intercept" | "remap" | "dropped";

export interface Shortcut {
  id: string;
  /** Keys exactly as the desktop lists them (QKeySequence portable text). */
  desktopKeys: string[];
  /** Keys the web app binds. Empty for gestures and for dropped shortcuts. */
  webKeys: string[];
  label: string;
  group: string;
  /** global: any screen. annotate: the annotation view. canvas: the image has focus. */
  scope: Scope;
  browser: BrowserVerdict;
  /** Intercepted only while the canvas has focus (the browser keeps it elsewhere). */
  canvasFocusOnly?: boolean;
}

export const GROUPS = [
  "Tools",
  "Move around the image",
  "Edit shapes",
  "Classes and review",
  "Images",
  "General",
] as const;

const s = (
  id: string, keys: string[], label: string, group: string, scope: Scope,
  browser: BrowserVerdict = "keep", extra: Partial<Shortcut> = {},
): Shortcut => ({ id, desktopKeys: keys, webKeys: scope === "gesture" ? [] : keys, label, group, scope, browser, ...extra });

export const SHORTCUTS: Shortcut[] = [
  // Tools
  s("tool.select", ["Esc"], "Select / pan tool (Esc again cancels a shape)", "Tools", "annotate"),
  s("tool.bbox", ["B"], "Bounding box", "Tools", "annotate"),
  s("tool.polygon", ["P"], "Polygon — click corners, Enter or right-click to close", "Tools", "annotate"),
  s("tool.obb", ["O"], "Rotated box — drag, then spin", "Tools", "annotate"),
  s("draw.close", ["Enter", "Double-click"], "Close the polygon being drawn", "Tools", "canvas"),
  s("draw.undo_point", ["Backspace"], "While drawing a polygon: take back the last point", "Tools", "canvas"),

  // Move around the image
  s("view.pan_space", ["Space + drag"], "Pan, from any tool", "Move around the image", "gesture", "intercept"),
  s("view.pan_middle", ["Middle-drag"], "Pan, from any tool", "Move around the image", "gesture", "intercept"),
  s("view.trackpad_pan", ["Two-finger scroll"], "Pan (trackpad)", "Move around the image", "gesture", "intercept"),
  s("view.pinch", ["Pinch"], "Zoom at the fingers (trackpad)", "Move around the image", "gesture", "intercept"),
  s("view.wheel", ["Mouse wheel", "Ctrl + scroll"], "Zoom at the cursor", "Move around the image", "gesture", "intercept"),
  s("view.zoom_in", ["+"], "Zoom in", "Move around the image", "canvas"),
  s("view.zoom_out", ["-"], "Zoom out", "Move around the image", "canvas"),
  // "0" is unchanged; Ctrl+F is taken from browser Find only while the canvas has focus.
  s("view.fit", ["0", "Ctrl+F"], "Fit the image to the window", "Move around the image", "annotate",
    "intercept", { canvasFocusOnly: true }),

  // Edit shapes
  s("edit.undo", ["Ctrl+Z"], "Undo", "Edit shapes", "annotate"),
  s("edit.redo", ["Ctrl+Y", "Ctrl+Shift+Z"], "Redo", "Edit shapes", "annotate"),
  s("edit.select_all", ["Ctrl+A"], "Select all shapes", "Edit shapes", "annotate"),
  s("edit.copy", ["Ctrl+C"], "Copy", "Edit shapes", "annotate"),
  s("edit.paste", ["Ctrl+V"], "Paste (onto whichever image is open)", "Edit shapes", "annotate"),
  s("edit.duplicate", ["Ctrl+D"], "Duplicate the selected shapes", "Edit shapes", "annotate",
    "intercept", { canvasFocusOnly: true }),
  s("edit.delete", ["Del", "Backspace"], "Delete the selected shapes", "Edit shapes", "annotate"),
  s("edit.nudge", ["Arrow keys"], "Nudge the selection 1 px (Shift: 10 px)", "Edit shapes", "canvas", "intercept"),
  s("edit.rotate", ["[", "]"], "Rotate polygon / rotated box 1° (Shift: 15°)", "Edit shapes", "canvas"),
  s("edit.move", ["Click, then drag"], "Move a shape (it must be selected first)", "Edit shapes", "gesture"),
  s("edit.save", ["Ctrl+S"], "Save now (also saves automatically)", "Edit shapes", "annotate", "intercept"),

  // Classes and review
  s("class.pick", ["1 … 9"], "Pick class 1–9; relabels the selection", "Classes and review", "canvas"),
  s("class.apply", ["L"], "Apply the active class to the selection", "Classes and review", "annotate"),
  s("review.mark", ["R"], "Mark the selection reviewed", "Classes and review", "annotate"),
  s("measure.selected", ["M"], "Measure the selected shape", "Classes and review", "annotate"),
  s("view.depth", ["D"], "Depth view (ToF captures)", "Classes and review", "annotate"),

  // Images
  s("image.next", ["Right", "PgDown"], "Next image (→ nudges instead while a shape is selected)", "Images", "annotate", "intercept"),
  s("image.prev", ["Left", "PgUp"], "Previous image (← nudges instead while a shape is selected)", "Images", "annotate", "intercept"),

  // General
  s("help.shortcuts", ["?", "F1"], "Show this list of shortcuts", "General", "global", "intercept"),
  s("app.dashboard", ["Ctrl+Shift+D"], "Back to the dashboard", "General", "annotate", "remap",
    { webKeys: ["Alt+Shift+D"] }),
  s("app.theme", ["Ctrl+T"], "Toggle light / dark theme", "General", "global", "remap",
    { webKeys: ["Alt+T"] }),
  s("app.quit", ["Ctrl+Q"], "Quit", "General", "global", "dropped", { webKeys: [] }),
];

/** The only places the web keys differ from the desktop's, approved 2026-10-07. */
export const REMAPS: Record<string, string[]> = {
  "app.dashboard": ["Alt+Shift+D"],
  "app.theme": ["Alt+T"],
  "app.quit": [],
};

/** Shortcuts the desktop binds in code but does not list in shortcuts.py. */
export const UNLISTED: { id: string; keys: string[]; label: string }[] = [
  { id: "edit.rotate_15", keys: ["{", "}"], label: "Rotate 15° (canvas)" },
  { id: "view.zoom_in_alt", keys: ["="], label: "Zoom in (same key as +)" },
];

/** German-keyboard names for the keys the lists and tooltips show. Display only: what a key press does never depends on this. */
const GERMAN_NAMES: Record<string, string> = { Ctrl: "Strg", Del: "Entf", PgUp: "Bild↑", PgDown: "Bild↓" };
/** [ ] { } have no key of their own on a German keyboard. */
const GERMAN_ALTGR: Record<string, [string, string]> = { "[": ["AltGr+8", "⌥+5"], "]": ["AltGr+9", "⌥+6"], "{": ["AltGr+7", "⌥+8"], "}": ["AltGr+0", "⌥+9"] };

export const isGermanUi = (): boolean => typeof navigator !== "undefined" && /^de\b/i.test(navigator.language || "");
const isMacUi = (): boolean => typeof navigator !== "undefined" && /mac/i.test(navigator.platform || "");

/** A key as the shortcut list and tooltips show it: "Ctrl+Z" reads "Strg+Z" and "[" reads "AltGr+8" for a German user. */
export function displayKey(key: string, german = isGermanUi(), mac = isMacUi()): string {
  if (!german) return key;
  const altgr = GERMAN_ALTGR[key];
  if (altgr) return altgr[mac ? 1 : 0];
  return key.replace(/\b(Ctrl|Del|PgUp|PgDown)\b/g, (w) => GERMAN_NAMES[w]);
}

export interface KeyContext {
  /** The annotation view is showing. */
  annotateView: boolean;
  /** The image canvas has keyboard focus. */
  canvasFocused: boolean;
  /** A text field, select or contenteditable has focus. */
  textFieldFocused: boolean;
  /** A selection exists (arrow keys nudge it instead of changing image). */
  hasSelection?: boolean;
  /** A polygon is being drawn (Backspace takes back a point instead of deleting). */
  drawingPolygon?: boolean;
  /** macOS: the Command key plays the part of Ctrl. */
  mac?: boolean;
}

export interface Resolved {
  id: string;
  /** For class.pick: 1–9. */
  arg?: number;
  /** The page should call preventDefault(): the browser has its own binding. */
  preventDefault: boolean;
}

const NAMED: Record<string, string> = {
  Escape: "Esc", Delete: "Del", PageDown: "PgDown", PageUp: "PgUp",
  ArrowRight: "Right", ArrowLeft: "Left", ArrowUp: "Up", ArrowDown: "Down", " ": "Space",
};
/** Characters whose Shift is part of the character itself. */
const SYMBOLS = new Set(["?", "[", "]", "{", "}", "+", "-", "="]);

type KeyEventLike = Pick<KeyboardEvent, "key" | "code" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">
  & { getModifierState?: (key: string) => boolean };

/** The key was typed with AltGr (Windows, Linux) or Option (Mac), which is how a German layout types [ ] { }. */
function typedWithAltGr(e: KeyEventLike, mac: boolean): boolean {
  return !!e.getModifierState?.("AltGraph") || (mac ? e.altKey : e.ctrlKey && e.altKey);
}

/** A key event as the same "Ctrl+Shift+Z" text the tables use. */
export function comboOf(e: KeyEventLike, mac = false): string {
  let key: string;
  let ctrl = mac ? e.metaKey : e.ctrlKey;
  let alt = e.altKey;
  // Letters come from what the layout types, so Z is the key labelled Z on a German (QWERTZ) or French keyboard.
  if (/^[A-Za-z]$/.test(e.key)) key = e.key.toUpperCase();
  // Symbols come from what the layout types too: on a German keyboard = is Shift+0 and [ ] { } are AltGr+8, 9, 7, 0.
  // The character is the key; an AltGr is how it was typed, not a modifier.
  else if (SYMBOLS.has(e.key)) {
    key = e.key;
    if (typedWithAltGr(e, mac)) { ctrl = false; alt = false; }
  }
  // Otherwise the physical key: with Alt held, macOS types "†" for Alt+T.
  else if (/^Key[A-Z]$/.test(e.code)) key = e.code.slice(3);
  else if (/^Digit[0-9]$/.test(e.code)) key = e.code.slice(5);
  else key = NAMED[e.key] ?? (e.key.length === 1 ? e.key.toUpperCase() : e.key);
  const parts: string[] = [];
  if (ctrl) parts.push("Ctrl");
  if (alt) parts.push("Alt");
  if (e.shiftKey && !SYMBOLS.has(key)) parts.push("Shift");
  parts.push(key);
  return parts.join("+");
}

/** Handled by context in resolve(), not by a plain key lookup. */
const CONTEXTUAL = new Set(["draw.undo_point", "edit.delete", "edit.nudge", "class.pick", "image.next", "image.prev"]);
const ARROWS = new Set(["Up", "Down", "Left", "Right"]);

const BY_COMBO = new Map<string, Shortcut>();
for (const sc of SHORTCUTS) {
  if (sc.scope === "gesture" || CONTEXTUAL.has(sc.id)) continue;
  for (const k of sc.webKeys) if (!k.includes(" ") && !k.includes("-click")) BY_COMBO.set(k.toUpperCase(), sc);
}
const imageNav = new Map<string, string>([
  ["RIGHT", "image.next"], ["PGDOWN", "image.next"], ["LEFT", "image.prev"], ["PGUP", "image.prev"],
]);
// Keys the desktop binds in code only (UNLISTED).
const EXTRA = new Map<string, string>([["{", "edit.rotate_15"], ["}", "edit.rotate_15"], ["=", "view.zoom_in"]]);

const byId = (id: string) => SHORTCUTS.find((x) => x.id === id);

/**
 * Which shortcut a key event triggers right now, if any. Returns null when the
 * browser should keep the key (text field focused, wrong screen, or not focused
 * on the canvas for a canvas-only key).
 */
export function resolve(
  e: KeyEventLike,
  ctx: KeyContext,
): Resolved | null {
  if (ctx.textFieldFocused) return null;
  const combo = comboOf(e, ctx.mac);
  const up = combo.toUpperCase();
  const done = (id: string, arg?: number, force = true): Resolved => ({ id, arg, preventDefault: force });

  // Contextual keys first.
  if (ctx.annotateView) {
    if (/^[1-9]$/.test(combo) && ctx.canvasFocused) return done("class.pick", Number(combo));
    if (combo === "Backspace") {
      if (ctx.drawingPolygon) return done("draw.undo_point");
      return done("edit.delete");
    }
    if (combo === "Del") return done("edit.delete");
    // Arrows nudge a selection, with or without Shift (Shift = 10 px); with nothing selected, a plain
    // Left/Right changes image (Shift+Left/Right is not a shortcut).
    const arrow = combo.replace(/^Shift\+/, "");
    if (ARROWS.has(arrow) && ctx.canvasFocused && ctx.hasSelection) return done("edit.nudge");
    const nav = imageNav.get(up);
    if (nav && !(ctx.hasSelection && ctx.canvasFocused && ARROWS.has(combo))) return done(nav);
  }

  const extraId = EXTRA.get(combo);
  const sc = BY_COMBO.get(up) ?? (extraId ? byId(extraId) ?? { id: extraId, scope: "canvas", browser: "keep" } as Shortcut : undefined);
  if (!sc) return null;

  if (sc.scope === "global") { /* any screen */ }
  else if (!ctx.annotateView) return null;
  else if (sc.scope === "canvas" && !ctx.canvasFocused) return null;
  if (sc.canvasFocusOnly && !ctx.canvasFocused) return null;

  return done(sc.id, undefined, sc.browser !== "keep" || sc.scope === "canvas");
}
